import { defineAthenaTenantConfig } from "@/configuration/ehr/athena/defaults";
import { PatientPrimaryDepartment } from "@/configuration/ehr/athena/types";
import { TprBiltmoreProviderNpi } from "@/configuration/groups/tpr/configuration";
import { getGroupConfigSubdomain } from "@/configuration/paths";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { logAndThrow } from "@/src/lib/logging/logger";
import { AppointmentType } from "@/src/models/appointment";
import type { Group } from "@/src/models/group";

export const TPR_BILTMORE_DEPARTMENT_ID = "648";

const TPR_BILTMORE_APPOINTMENT_TYPE_IDS: Partial<Record<AppointmentType, number>> = {
  [AppointmentType.INITIAL_CONSULTATION]: 3624,
  [AppointmentType.NEW_PATIENT_CHECKIN]: 3624,
  [AppointmentType.FOLLOW_UP]: 3116,
  [AppointmentType.EXISTING_PATIENT_CHECKIN]: 766,
  [AppointmentType.TELEHEALTH]: 3673,
};

enum TprBiltmoreOpenAppointmentTypeId {
  ANY_10 = 2959,
  ANY_15 = 2962,
  ANY_20 = 2966,
  ANY_30 = 2968,
  ESTABLISHED_PATIENT_10 = 210,
  ESTABLISHED_PATIENT_15 = 766,
  ESTABLISHED_PATIENT_20 = 211,
  ESTABLISHED_PATIENT_30 = 212,
  HOSPITAL_FOLLOW_UP_10 = 3005,
  HOSPITAL_FOLLOW_UP_15 = 3116,
  HOSPITAL_FOLLOW_UP_20 = 3006,
  HOSPITAL_FOLLOW_UP_30 = 3536,
  NEW_PATIENT_10 = 166,
  NEW_PATIENT_15 = 3624,
  NEW_PATIENT_30 = 3625,
  NEW_PATIENT_40 = 3626,
  TELEMED_10 = 3672,
  TELEMED_15 = 3673,
}

const TPR_BILTMORE_ANY_OPEN_APPOINTMENT_TYPE_IDS: ReadonlySet<number> = new Set([
  TprBiltmoreOpenAppointmentTypeId.ANY_10,
  TprBiltmoreOpenAppointmentTypeId.ANY_15,
  TprBiltmoreOpenAppointmentTypeId.ANY_20,
  TprBiltmoreOpenAppointmentTypeId.ANY_30,
]);

const TPR_BILTMORE_BOOKED_APPOINTMENT_TYPES: Record<number, AppointmentType> = {
  [TprBiltmoreOpenAppointmentTypeId.ANY_10]: AppointmentType.UNKNOWN,
  [TprBiltmoreOpenAppointmentTypeId.ANY_15]: AppointmentType.UNKNOWN,
  [TprBiltmoreOpenAppointmentTypeId.ANY_20]: AppointmentType.UNKNOWN,
  [TprBiltmoreOpenAppointmentTypeId.ANY_30]: AppointmentType.UNKNOWN,
  [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_10]: AppointmentType.EXISTING_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15]: AppointmentType.EXISTING_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_20]: AppointmentType.EXISTING_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_30]: AppointmentType.EXISTING_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_10]: AppointmentType.FOLLOW_UP,
  [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15]: AppointmentType.FOLLOW_UP,
  [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_20]: AppointmentType.FOLLOW_UP,
  [TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_30]: AppointmentType.FOLLOW_UP,
  [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_10]: AppointmentType.NEW_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15]: AppointmentType.NEW_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_30]: AppointmentType.NEW_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_40]: AppointmentType.NEW_PATIENT_CHECKIN,
  [TprBiltmoreOpenAppointmentTypeId.TELEMED_10]: AppointmentType.TELEHEALTH,
  [TprBiltmoreOpenAppointmentTypeId.TELEMED_15]: AppointmentType.TELEHEALTH,
};

export function getTprBiltmoreAppointmentTypeFromBookedEhrId(
  _providerEhrId: string,
  appointmentTypeId: number
): AppointmentType {
  return TPR_BILTMORE_BOOKED_APPOINTMENT_TYPES[appointmentTypeId] ?? AppointmentType.UNKNOWN;
}

const TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY = {
  ESTABLISHED_PATIENT_10: [
    TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_10,
    TprBiltmoreOpenAppointmentTypeId.ANY_10,
  ],
  ESTABLISHED_PATIENT_15: [
    TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_15,
    TprBiltmoreOpenAppointmentTypeId.ANY_15,
  ],
  ESTABLISHED_PATIENT_20: [
    TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_20,
    TprBiltmoreOpenAppointmentTypeId.ANY_20,
  ],
  ESTABLISHED_PATIENT_30: [
    TprBiltmoreOpenAppointmentTypeId.ESTABLISHED_PATIENT_30,
    TprBiltmoreOpenAppointmentTypeId.ANY_30,
  ],
  HOSPITAL_FOLLOW_UP_10: [
    TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_10,
    TprBiltmoreOpenAppointmentTypeId.ANY_10,
  ],
  HOSPITAL_FOLLOW_UP_15: [
    TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_15,
    TprBiltmoreOpenAppointmentTypeId.ANY_15,
  ],
  HOSPITAL_FOLLOW_UP_20: [
    TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_20,
    TprBiltmoreOpenAppointmentTypeId.ANY_20,
  ],
  HOSPITAL_FOLLOW_UP_30: [
    TprBiltmoreOpenAppointmentTypeId.HOSPITAL_FOLLOW_UP_30,
    TprBiltmoreOpenAppointmentTypeId.ANY_30,
  ],
  NEW_PATIENT_10: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_10, TprBiltmoreOpenAppointmentTypeId.ANY_10],
  NEW_PATIENT_15: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_15, TprBiltmoreOpenAppointmentTypeId.ANY_15],
  NEW_PATIENT_30: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_30, TprBiltmoreOpenAppointmentTypeId.ANY_30],
  TELEMED_10: [TprBiltmoreOpenAppointmentTypeId.TELEMED_10, TprBiltmoreOpenAppointmentTypeId.ANY_10],
  TELEMED_15: [TprBiltmoreOpenAppointmentTypeId.TELEMED_15, TprBiltmoreOpenAppointmentTypeId.ANY_15],
} as const;

// Athena's generic "Follow Up" type is marked "Do Not Use" and is intentionally not mapped.

type TprBiltmoreProviderOpenAppointmentTypes = {
  newPatient: readonly TprBiltmoreOpenAppointmentTypeId[];
  establishedPatient: readonly TprBiltmoreOpenAppointmentTypeId[];
  hospitalFollowUp: readonly TprBiltmoreOpenAppointmentTypeId[];
  telehealth: readonly TprBiltmoreOpenAppointmentTypeId[];
};

// TPR practitioners are configured and resolved by NPI; their Athena provider IDs are populated dynamically.
// Generic ANY types are included per use case only when their duration matches the specific appointment type.
const TPR_BILTMORE_PROVIDER_OPEN_APPOINTMENT_TYPES: Readonly<
  Record<TprBiltmoreProviderNpi, TprBiltmoreProviderOpenAppointmentTypes>
> = {
  // Diana Thayer
  [TprBiltmoreProviderNpi.DIANA_THAYER]: {
    newPatient: [],
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_30,
    hospitalFollowUp: [],
    telehealth: [],
  },
  // Danielle Sturm (Lorde)
  [TprBiltmoreProviderNpi.DANIELLE_STURM]: {
    newPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.NEW_PATIENT_15,
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_15,
    hospitalFollowUp: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.HOSPITAL_FOLLOW_UP_15,
    telehealth: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.TELEMED_15,
  },
  // Lorraine Homes
  [TprBiltmoreProviderNpi.LORRAINE_HOMES]: {
    newPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.NEW_PATIENT_15,
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_15,
    hospitalFollowUp: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.HOSPITAL_FOLLOW_UP_15,
    telehealth: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.TELEMED_15,
  },
  // Chris Lichtenwalter
  [TprBiltmoreProviderNpi.CHRIS_LICHTENWALTER]: {
    newPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.NEW_PATIENT_10,
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_10,
    hospitalFollowUp: [],
    telehealth: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.TELEMED_10,
  },
  // Marium Muzaffar
  [TprBiltmoreProviderNpi.MARIUM_MUZAFFAR]: {
    newPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.NEW_PATIENT_15,
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_15,
    hospitalFollowUp: [],
    telehealth: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.TELEMED_15,
  },
  // Peter Maki
  [TprBiltmoreProviderNpi.PETER_MAKI]: {
    newPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.NEW_PATIENT_15,
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_15,
    hospitalFollowUp: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.HOSPITAL_FOLLOW_UP_15,
    telehealth: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.TELEMED_15,
  },
  // Timothy Byrne
  [TprBiltmoreProviderNpi.TIMOTHY_BYRNE]: {
    newPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.NEW_PATIENT_15,
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_15,
    hospitalFollowUp: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.HOSPITAL_FOLLOW_UP_15,
    telehealth: [],
  },
  // Nickolaus Gramze
  [TprBiltmoreProviderNpi.NICKOLAUS_GRAMZE]: {
    newPatient: [TprBiltmoreOpenAppointmentTypeId.NEW_PATIENT_40],
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_20,
    hospitalFollowUp: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.HOSPITAL_FOLLOW_UP_20,
    telehealth: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.TELEMED_15,
  },
  // Akram Ibrahim
  [TprBiltmoreProviderNpi.AKRAM_IBRAHIM]: {
    newPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.NEW_PATIENT_10,
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_10,
    hospitalFollowUp: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.HOSPITAL_FOLLOW_UP_10,
    telehealth: [],
  },
  // Lawrence Kline
  [TprBiltmoreProviderNpi.LAWRENCE_KLINE]: {
    newPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.NEW_PATIENT_15,
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_15,
    hospitalFollowUp: [],
    telehealth: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.TELEMED_15,
  },
  // Neal Klein
  [TprBiltmoreProviderNpi.NEAL_KLEIN]: {
    newPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.NEW_PATIENT_15,
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_15,
    hospitalFollowUp: [],
    telehealth: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.TELEMED_15,
  },
  // Kristen Jensen
  [TprBiltmoreProviderNpi.KRISTEN_JENSEN]: {
    newPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.NEW_PATIENT_15,
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_15,
    hospitalFollowUp: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.HOSPITAL_FOLLOW_UP_15,
    telehealth: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.TELEMED_15,
  },
  // Jody Eckhardt
  [TprBiltmoreProviderNpi.JODY_ECKHARDT]: {
    newPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.NEW_PATIENT_30,
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_30,
    hospitalFollowUp: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.HOSPITAL_FOLLOW_UP_30,
    telehealth: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.TELEMED_15,
  },
  // Kevin Murphy
  [TprBiltmoreProviderNpi.KEVIN_MURPHY]: {
    newPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.NEW_PATIENT_30,
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_30,
    hospitalFollowUp: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.HOSPITAL_FOLLOW_UP_30,
    telehealth: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.TELEMED_15,
  },
  // Elizabeth Steffens
  [TprBiltmoreProviderNpi.ELIZABETH_STEFFENS]: {
    newPatient: [],
    establishedPatient: TPR_BILTMORE_OPEN_APPOINTMENT_TYPE_IDS_WITH_MATCHING_ANY.ESTABLISHED_PATIENT_30,
    hospitalFollowUp: [],
    telehealth: [],
  },
};

function isTprBiltmoreProviderNpi(providerNpi: string): providerNpi is TprBiltmoreProviderNpi {
  return Object.hasOwn(TPR_BILTMORE_PROVIDER_OPEN_APPOINTMENT_TYPES, providerNpi);
}

export function isTprBiltmoreGroup(group: Group): boolean {
  return getGroupConfigSubdomain(group) === ConfigSubdomain.TPR;
}

export function getTprBiltmoreAppointmentTypeId(group: Group, appointmentType: AppointmentType): number | null {
  if (!isTprBiltmoreGroup(group)) return null;

  const appointmentTypeId = TPR_BILTMORE_APPOINTMENT_TYPE_IDS[appointmentType];

  if (appointmentTypeId === undefined) {
    logAndThrow("No TPR Biltmore appointment type configured", { appointmentType });
  }

  return appointmentTypeId;
}

export function getTprBiltmoreOpenAppointmentTypeIds(
  group: Group,
  providerNpi: string | null | undefined,
  appointmentType: AppointmentType
): readonly number[] | null {
  if (!isTprBiltmoreGroup(group)) return null;

  if (!providerNpi) {
    logAndThrow("Provider NPI is required to resolve TPR Biltmore open appointment types", { appointmentType });
  }

  if (!isTprBiltmoreProviderNpi(providerNpi)) {
    logAndThrow("No TPR Biltmore open appointment types configured for provider", { providerNpi, appointmentType });
  }

  const mapping = TPR_BILTMORE_PROVIDER_OPEN_APPOINTMENT_TYPES[providerNpi];

  let useCaseTypeIds: readonly TprBiltmoreOpenAppointmentTypeId[];

  switch (appointmentType) {
    case AppointmentType.INITIAL_CONSULTATION:
    case AppointmentType.NEW_PATIENT_CHECKIN:
      useCaseTypeIds = mapping.newPatient;
      break;
    case AppointmentType.FOLLOW_UP:
      useCaseTypeIds = mapping.hospitalFollowUp;
      break;
    case AppointmentType.EXISTING_PATIENT_CHECKIN:
      useCaseTypeIds = mapping.establishedPatient;
      break;
    case AppointmentType.TELEHEALTH:
      useCaseTypeIds = mapping.telehealth;
      break;
    default:
      logAndThrow("No TPR Biltmore open appointment types configured for appointment type", {
        providerNpi,
        appointmentType,
      });
  }

  return useCaseTypeIds;
}

export function getTprBiltmoreBookedTypeIdFromOpenTypeId(
  group: Group,
  providerNpi: string | null | undefined,
  openAppointmentTypeId: string,
  internalAppointmentType: AppointmentType | null
): number | null {
  if (!isTprBiltmoreGroup(group)) return null;

  if (!providerNpi) {
    logAndThrow("No TPR Biltmore provider NPI configured for open appointment type", {
      openAppointmentTypeId,
      internalAppointmentType,
    });
  }

  if (!internalAppointmentType) {
    logAndThrow("No TPR Biltmore booking type configured for open appointment type", {
      providerNpi,
      openAppointmentTypeId,
    });
  }

  const eligibleOpenAppointmentTypeIds = getTprBiltmoreOpenAppointmentTypeIds(
    group,
    providerNpi,
    internalAppointmentType
  );
  const openAppointmentTypeIdNumber = Number(openAppointmentTypeId);

  if (!eligibleOpenAppointmentTypeIds?.includes(openAppointmentTypeIdNumber)) {
    logAndThrow("Open appointment type is not configured for TPR Biltmore provider and appointment type", {
      providerNpi,
      openAppointmentTypeId,
      internalAppointmentType,
    });
  }

  if (!TPR_BILTMORE_ANY_OPEN_APPOINTMENT_TYPE_IDS.has(openAppointmentTypeIdNumber)) {
    return openAppointmentTypeIdNumber;
  }

  const pairedAppointmentTypeId = eligibleOpenAppointmentTypeIds.find(
    (appointmentTypeId) => !TPR_BILTMORE_ANY_OPEN_APPOINTMENT_TYPE_IDS.has(appointmentTypeId)
  );

  if (pairedAppointmentTypeId === undefined) {
    logAndThrow("No specific TPR Biltmore appointment type is paired with generic open appointment type", {
      providerNpi,
      openAppointmentTypeId,
      internalAppointmentType,
    });
  }

  return pairedAppointmentTypeId;
}

export const tprAthenaConfig = defineAthenaTenantConfig({
  subdomain: ConfigSubdomain.TPR,
  core: {
    practiceId: 600,
    departments: {
      [TPR_BILTMORE_DEPARTMENT_ID]: { name: "Biltmore Cardiology - Camelback", assignee: null },
    },
    patientPrimaryDepartment: PatientPrimaryDepartment.RECEIVING_LOCATION,
    defaultReceivingLocationEhrId: TPR_BILTMORE_DEPARTMENT_ID,
  },
  patient: {
    consent: {
      consenttocall: true,
      consenttotext: true,
      isprivacynoticeonfile: true,
      medicationhistoryconsentverified: null,
    },
  },
  scheduling: {
    bookedAppointmentType: getTprBiltmoreAppointmentTypeFromBookedEhrId,
    resolveAppointmentTypeId: ({ group, appointmentType }) => {
      const appointmentTypeId = getTprBiltmoreAppointmentTypeId(group, appointmentType);

      if (appointmentTypeId === null) {
        logAndThrow("No TPR Biltmore appointment type configured", { appointmentType });
      }

      return appointmentTypeId;
    },
  },
});
